import * as React from 'react';
import { RefObject } from 'react';

import { Box, CircularProgress, Typography } from '@mui/material';

import { RadioClickData } from '@/models/radio.click.data';

import { GroupedRadioClickData } from '../hooks';
import { ScrollBox } from '../styles';
import { RadioClicksItemComponent } from './RadioClicksItem';
import { RadioClicksTimestamp } from './RadioClicksTimestamp';

interface RadioClicksListProps {
  containerRef: RefObject<HTMLDivElement>;
  groupedData: GroupedRadioClickData;
  handleClick: (
    event: React.MouseEvent<HTMLButtonElement>,
    item: RadioClickData,
  ) => void;
  isLoading?: boolean;
  handleWheel: (e: React.WheelEvent<HTMLDivElement>) => void;
}

export const RadioClicksList = ({
  containerRef,
  groupedData,
  handleClick,
  isLoading = false,
  handleWheel,
}: RadioClicksListProps) => {
  const groupedEntries = Object.entries(groupedData);

  return (
    <ScrollBox
      ref={containerRef}
      onWheel={handleWheel}
      sx={{
        overflowY: 'auto',
        height: '100%',
        maxHeight: '100%',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.2)',
          borderRadius: '4px',
        },
      }}
    >
      {isLoading ? (
        <Box
          sx={{
            display: 'inline-flex',
            height: '100%',
            alignItems: 'center',
            paddingX: 2,
            gap: 1,
          }}
        >
          <CircularProgress color="error" size={16} />
          <Typography fontSize={12} fontWeight={500}>
            Loading radio clicks...
          </Typography>
        </Box>
      ) : Object.entries(groupedData).length === 0 ? (
        <Box
          sx={{
            display: 'inline-flex',
            height: '100%',
            alignItems: 'center',
            paddingX: 2,
          }}
        >
          <Typography fontSize={12} fontWeight={500}>
            No recent radio clicks
          </Typography>
        </Box>
      ) : (
        groupedEntries.map(([key, data]) => (
          <Box
            key={key}
            sx={{ display: 'inline-flex', height: '100%', maxHeight: '30px' }}
          >
            <RadioClicksTimestamp timestamp={key} />
            {data.map((item, itemIndex) => (
              <React.Fragment key={itemIndex}>
                <RadioClicksItemComponent
                  item={item}
                  handleClick={handleClick}
                  isFirst={itemIndex === 0}
                  isLast={itemIndex === data.length - 1}
                />
              </React.Fragment>
            ))}
          </Box>
        ))
      )}
    </ScrollBox>
  );
};
